import { PopoverComponentCss } from './PopoverComponent.tpl.js';

/**
 * <im-popover> — Light DOM popover.
 *
 * Поведение и UX ПОЛНОСТЬЮ идентичны Shadow DOM версии.
 *
 * Контракт разметки:
 *  - .im-popover-trigger
 *  - .im-popover-hover
 *  - .im-popover-full
 */
class PopoverComponent extends HTMLElement {
    constructor() {
        super(...arguments);
        /** Hover всегда открыт (always-visible) */
        this.alwaysShowHover = false;
        this._onContentCloseClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this._applyFullOpen(false, 'content-close');
        };
    }
    static _ensureStyles() {
        if (PopoverComponent._stylesInjected)
            return;
        const id = 'im-popover-styles';
        if (!document.getElementById(id)) {
            const style = document.createElement('style');
            style.id = id;
            style.textContent = PopoverComponentCss;
            document.head.appendChild(style);
        }
        PopoverComponent._stylesInjected = true;
    }
    // -------------------- lifecycle --------------------
    connectedCallback() {
        PopoverComponent._ensureStyles();
        this._initElements();
        this._initAria();
        this._bindHover();
        this._bindClick();
        this._initContentCloseButton();
        this._initAlwaysVisibleBehavior();
        this._applyHoverOpen(this.dataset.hoverOpen === 'true', 'init', false);
        this._applyFullOpen(this.dataset.open === 'true', 'init', false);
    }
    disconnectedCallback() {
        this._unbindHover();
        this._unbindClick();
        this._teardownGlobals();
        if (this._contentCloseBtn) {
            this._contentCloseBtn.removeEventListener('click', this._onContentCloseClick);
        }
    }
    static get observedAttributes() {
        return ['data-hover-open', 'data-open'];
    }
    attributeChangedCallback(name, _old, nv) {
        if (this._suppressEcho)
            return;
        if (name === 'data-hover-open') {
            this._applyHoverOpen(nv === 'true', 'attr', false);
        }
        else if (name === 'data-open') {
            this._applyFullOpen(nv === 'true', 'attr', false);
        }
    }
    // -------------------- init --------------------
    _initElements() {
        this._triggerEl = this.querySelector('.im-popover-trigger');
        this._hoverPanel = this.querySelector('.im-popover-hover');
        this._fullPanel = this.querySelector('.im-popover-full');
        if (!this._triggerEl || !this._hoverPanel || !this._fullPanel) {
            throw new Error('im-popover: invalid markup');
        }
    }
    _initAria() {
        const t = this._triggerEl;
        t.setAttribute('aria-haspopup', 'dialog');
        t.setAttribute('aria-expanded', this.dataset.open === 'true' ? 'true' : 'false');
        if (!this._hoverPanel.id) {
            this._hoverPanel.id = `im-popover-hover-${Math.random().toString(36).slice(2)}`;
        }
        if (!this._fullPanel.id) {
            this._fullPanel.id = `im-popover-full-${Math.random().toString(36).slice(2)}`;
        }
        t.setAttribute('aria-controls', `${this._hoverPanel.id} ${this._fullPanel.id}`);
    }
    // -------------------- helpers --------------------
    _show(el) {
        el.classList.add('is-open');
    }
    _hide(el) {
        el.classList.remove('is-open');
    }
    // -------------------- hover --------------------
    _hoverHasContent() {
        if (this._hoverPanel.textContent?.trim())
            return true;
        return Array.from(this._hoverPanel.children).some((node) => {
            const el = node;
            if (el.hidden || el.getAttribute('aria-hidden') === 'true')
                return false;
            if (el instanceof HTMLInputElement && el.type === 'hidden')
                return false;
            return true;
        });
    }
    _hoverHasLoading() {
        if (this._hoverPanel.classList.contains('btn-loading'))
            return true;
        return !!this._hoverPanel.querySelector('.btn-loading');
    }
    _bindHover() {
        this._unbindHover();
        this._hoverAc = new AbortController();
        const enter = () => {
            window.clearTimeout(this._hoverCloseTimer);
            this._hoverOpenTimer = window.setTimeout(() => {
                this._applyHoverOpen(true, 'hover');
            }, 70);
        };
        const leave = () => {
            if (this.alwaysShowHover)
                return;
            if (this._hoverHasLoading())
                return;
            window.clearTimeout(this._hoverOpenTimer);
            this._hoverCloseTimer = window.setTimeout(() => {
                if (this._hoverHasLoading())
                    return;
                this._applyHoverOpen(false, 'mouseleave');
            }, 120);
        };
        this._triggerEl.addEventListener('mouseenter', enter, { signal: this._hoverAc.signal });
        this._triggerEl.addEventListener('mouseleave', leave, { signal: this._hoverAc.signal });
        this._hoverPanel.addEventListener('mouseenter', () => window.clearTimeout(this._hoverCloseTimer), { signal: this._hoverAc.signal });
        this._hoverPanel.addEventListener('mouseleave', leave, { signal: this._hoverAc.signal });
    }
    _unbindHover() {
        this._hoverAc?.abort();
        this._hoverAc = undefined;
        window.clearTimeout(this._hoverOpenTimer);
        window.clearTimeout(this._hoverCloseTimer);
    }
    _applyHoverOpen(open, _reason, reflect = true) {
        const hasContent = this._hoverHasContent();
        if (open && !hasContent) {
            open = false;
            reflect = true;
        }
        if (this.alwaysShowHover && !open && hasContent)
            return;
        const prev = this.dataset.hoverOpen === 'true';
        if (reflect && open !== prev) {
            this._reflectState('data-hover-open', open ? 'true' : 'false');
        }
        if (open)
            this._show(this._hoverPanel);
        else
            this._hide(this._hoverPanel);
    }
    // -------------------- full --------------------
    _fullHasLoading() {
        if (this._fullPanel.classList.contains('btn-loading'))
            return true;
        return !!this._fullPanel.querySelector('.btn-loading');
    }
    _bindClick() {
        this._unbindClick();
        this._clickAc = new AbortController();
        this._triggerEl.addEventListener('click', (e) => {
            e.preventDefault();
            const isOpen = this.dataset.open === 'true';
            this._applyFullOpen(!isOpen, 'click');
        }, { signal: this._clickAc.signal });
    }
    _unbindClick() {
        this._clickAc?.abort();
        this._clickAc = undefined;
    }
    _applyFullOpen(open, _reason, reflect = true) {
        const prev = this.dataset.open === 'true';
        if (reflect && open !== prev) {
            this._reflectState('data-open', open ? 'true' : 'false');
        }
        if (open)
            this._show(this._fullPanel);
        else
            this._hide(this._fullPanel);
        if (open)
            this._setupGlobals();
        else
            this._teardownGlobals();
    }
    // -------------------- always-visible --------------------
    _initAlwaysVisibleBehavior() {
        const update = () => {
            let alwaysVisible;
            if (this._hoverPanel.classList.contains('always-visible')) {
                alwaysVisible = true;
            }
            else {
                const nodes = Array.from(this._hoverPanel.children);
                alwaysVisible = nodes.some((n) => n.classList.contains('always-visible'));
            }
            if (alwaysVisible) {
                this.alwaysShowHover = this._hoverHasContent();
                this._applyHoverOpen(this.alwaysShowHover, 'always-visible');
            }
            else {
                this.alwaysShowHover = false;
                if (!this._hoverHasLoading() && this.dataset.hoverOpen === 'true') {
                    this._applyHoverOpen(false, 'always-visible-off');
                }
            }
        };
        update();
    }
    // -------------------- content close --------------------
    _initContentCloseButton() {
        let found = null;
        const scan = () => {
            found = this._fullPanel.querySelector('.popover-close, [data-action="close"]');
            if (found && this._contentCloseBtn !== found) {
                this._contentCloseBtn?.removeEventListener('click', this._onContentCloseClick);
                this._contentCloseBtn = found;
                found.addEventListener('click', this._onContentCloseClick);
            }
        };
        scan();
    }
    // -------------------- globals --------------------
    _setupGlobals() {
        this._teardownGlobals();
        const ac = new AbortController();
        this._globalAc = ac;
        document.addEventListener('pointerdown', (ev) => {
            const t = ev.target;
            if (t?.closest('im-dialog'))
                return;
            if (!this.contains(t)) {
                if (!this._fullHasLoading()) {
                    this._applyFullOpen(false, 'outside');
                }
            }
        }, { capture: true, signal: ac.signal });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                if (!this._fullHasLoading()) {
                    this._applyFullOpen(false, 'escape');
                }
            }
        }, { signal: ac.signal });
    }
    _teardownGlobals() {
        this._globalAc?.abort();
        this._globalAc = undefined;
    }
    // -------------------- reflect --------------------
    _reflectState(attr, value) {
        this._suppressEcho = true;
        if (attr === 'data-hover-open')
            this.dataset.hoverOpen = value;
        else
            this.dataset.open = value;
        this._suppressEcho = false;
    }
    // -------------------- public API --------------------
    open(reason = 'api') {
        this._applyFullOpen(true, reason);
    }
    close(reason = 'api') {
        this._applyFullOpen(false, reason);
    }
    toggle(reason = 'api') {
        this._applyFullOpen(!(this.dataset.open === 'true'), reason);
    }
}
// -------------------- styles --------------------
PopoverComponent._stylesInjected = false;

export { PopoverComponent };
